/**
 * Progressively enhances the collection grid's real, crawlable numbered pagination
 * (`snippets/pagination-controls.liquid`) into a single "Load more" button.
 *
 * This is enhancement only: the paginated `{% paginate %}` markup and its real `paginate.next.url`
 * link are always rendered server-side first (see `sections/main-collection.liquid`), so the page
 * works — and is crawlable — without this script. When it runs, it hides the numbered nav and
 * replaces it with a button that fetches the next page via the Section Rendering API (the same
 * `@theme/section-renderer` helper Horizon's own infinite-scroll component uses) and appends the new
 * cards into the existing grid, rather than reloading the numbered-page-only experience.
 *
 * Deliberately not infinite scroll: loading only happens on an explicit click.
 *
 * Survives a re-render. Changing the sort order or a filter makes Horizon *morph* new markup into
 * the existing section (`assets/morph.js`) — the nav element itself is kept, its `hidden` flag is
 * reset, and our button, which is absent from the server HTML, is removed. So the state is read
 * from the DOM on every mutation instead of from a "already enhanced" marker: if there is a next page and
 * no button, build one.
 */
import { sectionRenderer } from '@theme/section-renderer';

const ROOT_SELECTOR = '[data-load-more-enabled="true"]';

/**
 * Hides the numbered nav for real.
 *
 * `element.hidden = true` on its own does nothing here: the theme's own `.pagination { display:
 * flex }` rule outranks the user-agent `[hidden] { display: none }`, so the numbered pagination
 * stayed visible underneath the button — reporting whatever page was last fetched. The inline
 * style wins over the stylesheet; `hidden` stays set so assistive technology agrees.
 *
 * @param {HTMLElement} nav
 */
function hideNav(nav) {
  nav.hidden = true;
  nav.style.display = 'none';
}

/**
 * @param {Element | null} nav
 * @returns {HTMLAnchorElement | null}
 */
function getNextLink(nav) {
  const lastItem = nav?.querySelector('.pagination__list > li:last-child a[href]');
  return lastItem instanceof HTMLAnchorElement ? lastItem : null;
}

/**
 * @param {Element} root
 */
function enhance(root) {
  const sectionId = root.getAttribute('section-id');
  const nav = root.querySelector('.pagination');
  if (!sectionId || !nav || !root.querySelector('[ref="grid"]')) return;

  // Already enhanced and still intact — just make sure a morph did not reveal the numbered nav.
  if (root.querySelector('.load-more-button')) {
    hideNav(nav);
    return;
  }

  if (!getNextLink(nav)) {
    // Nothing left to load. Keep the nav hidden only when this page ran out *because of us*;
    // a visitor who lands straight on the last numbered page still needs the nav to get back.
    if (root.dataset.loadMoreExhausted === 'true') hideNav(nav);
    return;
  }

  delete root.dataset.loadMoreExhausted;
  root.querySelectorAll('.load-more-status').forEach((el) => el.remove());

  const label = root.getAttribute('data-load-more-label') || 'Load more';

  const status = document.createElement('div');
  status.className = 'visually-hidden load-more-status';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'load-more-button button-secondary';
  button.textContent = label;

  hideNav(nav);
  nav.insertAdjacentElement('afterend', button);
  button.insertAdjacentElement('afterend', status);

  button.addEventListener('click', async () => {
    // Re-read: a morph may have handed us different elements since this listener was bound.
    const currentNav = root.querySelector('.pagination');
    const grid = root.querySelector('[ref="grid"]');
    const currentNextLink = getNextLink(currentNav);
    if (!currentNav || !grid || !currentNextLink) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const url = new URL(currentNextLink.href, window.location.href);
    // Keep a clean copy for the address bar: `getSectionHTML` appends `section_id` to the URL it is
    // handed, and that internal parameter must never end up in the visitor's address bar or in a
    // link they copy.
    const historyUrl = url.toString();
    const previousCount = grid.querySelectorAll(':scope > [ref="cards[]"]').length;

    // `useCache: true` deliberately reuses the same cache Horizon's own infinite-scroll component
    // (`PaginatedList`) already prefetches this exact next-page URL into on connect — the click
    // usually resolves instantly instead of triggering a second network request for the same page.
    let html;
    try {
      html = await sectionRenderer.getSectionHTML(sectionId, true, new URL(url));
    } catch {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      return;
    }

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = parsed.querySelector('[ref="grid"]');
    const newNav = parsed.querySelector('.pagination');

    if (newGrid) {
      grid.append(...newGrid.querySelectorAll(':scope > [ref="cards[]"]'));
    }

    history.pushState('', '', historyUrl);

    const addedCount = grid.querySelectorAll(':scope > [ref="cards[]"]').length - previousCount;
    status.textContent = addedCount === 1 ? '1 more product loaded' : `${addedCount} more products loaded`;

    // Always adopt the nav of the page just loaded, so the page it reports as current matches the
    // address bar even on the final click — otherwise the hidden nav keeps claiming the
    // second-to-last page, which is what a visitor sees if anything reveals it again.
    if (newNav) currentNav.innerHTML = newNav.innerHTML;
    hideNav(currentNav);

    if (getNextLink(currentNav)) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      // Keep focus on the button for keyboard users, but never move the viewport. A bare `focus()`
      // scrolls the button into view, and the button sits *below* the cards that were just
      // appended — so every click threw the reader to the very bottom of the page.
      button.focus({ preventScroll: true });
    } else {
      // Everything is on the page now: no button, and no numbered nav to contradict it.
      root.dataset.loadMoreExhausted = 'true';
      button.remove();
      status.remove();
    }
  });
}

function enhanceAll() {
  document.querySelectorAll(ROOT_SELECTOR).forEach(enhance);
}

enhanceAll();

// Deliberately a timer and not `requestAnimationFrame`: rAF callbacks are suspended while the tab
// is in the background, so a mutation that arrived then would leave the "queued" flag stuck and
// silently disable this observer for the rest of the page's life. The short delay also lets the
// morph finish before we look at the result.
let queued = 0;
new MutationObserver(() => {
  clearTimeout(queued);
  queued = setTimeout(enhanceAll, 100);
}).observe(document.body, { childList: true, subtree: true });
